import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { QCResultStatsQueryDTO } from "@/modules/qc/domain/contracts/result";
import { Button, Card } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { formatDateForInput } from "@/shared/lib/date-time";
import { HelpCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { useQCResultStats } from "../api/queries";
import { QCResultTour, useQCResultTour } from "../tour/QCResultTour";
import { QCResultByDepartment } from "../ui/QCResultByDepartment";
import { QCResultCharts } from "../ui/QCResultCharts";
import { QCResultFailedRecords } from "../ui/QCResultFailedRecords";
import { QCResultFilter } from "../ui/QCResultFilter";
import { QCResultProblems } from "../ui/QCResultProblems";
import { QCResultStatsCards } from "../ui/QCResultStatsCards";
import { QCResultTrend } from "../ui/QCResultTrend";

export function QCResultPage() {
  const nav = useNavigate();
  const tour = useQCResultTour();
  const searchParams = useSearch({ strict: false }) as Record<
    string,
    string | undefined
  >;
  const search = {
    departmentId: searchParams.departmentId,
    startDate: searchParams.startDate,
    endDate: searchParams.endDate,
  };

  const [departmentId, setDepartmentId] = useState<string>(
    search?.departmentId || "",
  );
  const [startDate, setStartDate] = useState<string>(
    search?.startDate ||
      formatDateForInput(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
  );
  const [endDate, setEndDate] = useState<string>(
    search?.endDate || formatDateForInput(new Date()),
  );

  const query: QCResultStatsQueryDTO = useMemo(
    () => ({
      departmentId: departmentId || undefined,
      startDate: startDate ? `${startDate}T00:00:00.000Z` : undefined,
      endDate: endDate ? `${endDate}T23:59:59.999Z` : undefined,
    }),
    [departmentId, startDate, endDate],
  );

  const { data: stats, isLoading } = useQCResultStats(query);

  const handleFilterChange = () => {
    nav({
      to: "/app/qc/result",
      search: (prev) => ({
        ...prev,
        departmentId: departmentId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
    });
  };

  const handleRecordClick = (qcStandardId: string, recordId: string) => {
    nav({
      to: `/app/qc/${qcStandardId}/record/${recordId}`,
    });
  };

  return (
    <>
      <Header />
      <Main>
        <QCResultTour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-bold text-2xl">ສະຫຼຸບຜົນ QC</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="mt-1 text-muted-foreground">
                ສະຖິຕິແລະການວິເຄາະຜົນກວດ QC
              </p>
            </div>
          </div>

          {/* Filters */}
          <QCResultFilter
            departmentId={departmentId}
            startDate={startDate}
            endDate={endDate}
            onDepartmentChange={setDepartmentId}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onFilterChange={handleFilterChange}
          />

          {isLoading ? (
            <div className="flex min-h-[400px] items-center justify-center">
              <div className="text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
                <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
              </div>
            </div>
          ) : stats ? (
            <>
              {/* Overall Statistics */}
              <QCResultStatsCards stats={stats} />

              {/* Pass/Fail Chart */}
              <QCResultCharts stats={stats} />

              {/* Statistics by Department */}
              <QCResultByDepartment stats={stats} />

              {/* Common Problems */}
              <QCResultProblems stats={stats} />

              {/* Recent Failed Records */}
              <QCResultFailedRecords
                stats={stats}
                onRecordClick={handleRecordClick}
              />

              {/* Trend by Date */}
              <QCResultTrend stats={stats} />

              {/* Empty State */}
              {stats.overall.total === 0 && (
                <Card className="p-12 text-center">
                  <p className="text-lg text-muted-foreground">
                    ບໍ່ມີຂໍ້ມູນ QC ໃນຊ່ວງເວລາທີ່ເລືອກ
                  </p>
                </Card>
              )}
            </>
          ) : null}
        </div>
      </Main>
    </>
  );
}
